import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { FormService } from './../../../../../api/form.service';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import uuid from '../../../../../utils/uuid';
import { HelperService } from '../../../../../utils/helper.service';

@Component({
  selector: 'iv-period-annual-rent',
  templateUrl: './period-annual-rent.component.html',
  styleUrls: ['./period-annual-rent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodAnnualRentComponent implements OnInit, OnDestroy {

  @Input() transactionId;
  @Input() feeAnnualRentPeriod;
  @Input() feeAnnualRentPeriodIndex: number = 0;
  @Input() currencySymbol = '';
  @Input() currencyLabel = '';
  @Input() lastChange;
  @Input() disabled = false;
  @Input() outgoings;
  @Input() feeRateCondition = 'percentage';
  @Input() calculationMetricCondition: string = 'size';

  @Output() deleteFeeAnnualRentPeriod;
  @Output() saveFeeAnnualRentPeriod;

  feeAnnualRentPeriodForm: UntypedFormGroup;
  feeAnnualRentPeriodFormChanges$;
  uuidFeeAnnualRentPeriodForm = uuid();

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private router: Router,
    private helperService: HelperService) {
    this.deleteFeeAnnualRentPeriod = new EventEmitter();
    this.saveFeeAnnualRentPeriod = new EventEmitter();
  }

  ngOnInit() {
    this.setupForm();
  }

  setupForm() {
    const objForm = {
      rent_increase: [this.updateRentIncrease(this.feeAnnualRentPeriod.rent_increase)],
      annual_rent: [this.feeAnnualRentPeriod.annual_rent],
      annual_outgoing: [this.feeAnnualRentPeriod.annual_outgoing],
      months: [{ value: this.feeAnnualRentPeriod.months, disabled: this.disabled }],
      annual_car_park: [this.feeAnnualRentPeriod.annual_car_park],
      incentive: [{
        value: this.helperService.transformNumber(this.feeAnnualRentPeriod.incentive),
        disabled: true
      }],
      property_size: [{ value: this.feeAnnualRentPeriod.property_size, disabled: false }],
      annual_transaction_amount: [{ value: this.feeAnnualRentPeriod.annual_transaction_amount, disabled: false }],
      fee_rate: [
        { value: this.feeAnnualRentPeriod.fee_rate, disabled: false },
        Validators.compose([this.feeRateCondition !== 'percentage' ? Validators.max(12) : null ])
      ],
      annual_cbre_fee: [{ value: this.feeAnnualRentPeriod.annual_cbre_fee, disabled: false }]
    };
    this.feeAnnualRentPeriodForm = this.fb.group(objForm);
    this.formService.addLoadedForm(this.feeAnnualRentPeriodForm, this.uuidFeeAnnualRentPeriodForm);
    this.setupFormSubscription();
  }

  setupFormSubscription() {
    if (this.feeAnnualRentPeriodFormChanges$) {
      this.feeAnnualRentPeriodFormChanges$.unsubscribe();
    }
    this.feeAnnualRentPeriodFormChanges$ = this.feeAnnualRentPeriodForm.valueChanges
      .pipe(debounceTime(1000),distinctUntilChanged())
      .subscribe(changes => {
        if ((changes.annual_rent && changes.annual_rent !== this.feeAnnualRentPeriod.annual_rent) ||
          (changes.rent_increase && changes.rent_increase !== this.feeAnnualRentPeriod.rent_increase) ||
          (changes.annual_car_park && changes.annual_car_park !== this.feeAnnualRentPeriod.annual_car_park)) {
          changes.execute_get_request = true;
        }

        this.feeAnnualRentPeriod = { ...this.feeAnnualRentPeriod, ...changes, 'index': this.feeAnnualRentPeriodIndex };
        this.saveFeeAnnualRentPeriod.emit(this.feeAnnualRentPeriod);
      });
  }

  disableInput(formField, feeAnnualRentPeriodIndex, outgoings?) {
    switch (formField) {
      case 'rent_increase':
        if (feeAnnualRentPeriodIndex === 0) {
          return true;
        }
        break;
      case 'annual_rent':
      case 'annual_car_park':
        if (feeAnnualRentPeriodIndex > 0) {
          return true;
        }
        break;
      case 'annual_outgoing':
        if (feeAnnualRentPeriodIndex > 0 && outgoings === 'Increase Outgoings') {
          return true;
        }
        break;
    }
    return false;
  }

  updateRentIncrease(rentIncrease) {
    if (+rentIncrease !== 0 && this.feeAnnualRentPeriodIndex === 0) {
      return 0;
    }
    return rentIncrease;
  }

  ngOnDestroy() {
    if (this.feeAnnualRentPeriodFormChanges$) {
      this.feeAnnualRentPeriodFormChanges$.unsubscribe();
    }
  }

  disableDeleteButton() {
    return this.feeAnnualRentPeriodIndex === 0 ? true : false;
  }

  redirectToPropertyPage() {
    this.router.navigate([`/lease/${this.transactionId}/property`], {'fragment': 'incentive-details'});
  }

  getTooltip(field, fieldType) {
    let tooltip = '';
    if (this.feeAnnualRentPeriodForm.get(field)) {
      tooltip = this.feeAnnualRentPeriodForm.get(field).value;
      if (tooltip !== '') {
        switch (fieldType) {
          case 'number':
            tooltip = this.currencySymbol + ' ' + tooltip;
          break;
        }
      }
    }
    // Return tooltip only when is greater than 10 digits/characters
    if (tooltip.length > 10 ) {
      return tooltip;
    }
  }

  getFeeRateSuffix() {
    return this.feeRateCondition === 'percentage' ? '%' : '';
  }

}
